/*
  AuthBase Class
  認証の基本機能を提供する
  (Firebase Authentication の wrapper)
*/
import * as firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/analytics';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import jwt_decode from 'jwt-decode';
import PubSub from 'pubsub-js';

// Utils
import {closeCurrentSocket} from 'js/utils/helpers/socket';

export const AUTH_ERROR_CODES = {
  RECAPTCHA_NOT_PREPARED: 'recaptcha-not-prepared',
  USER_NOT_SIGNED_IN: 'user-not-signed-in',
};

export const SIGN_IN_PROVIDER_VALUE = {
  emailPassword: 'password',
  phoneNumber: 'phone',
};

export default class AuthBase {
  constructor() {
    this.isInitialized = false;
    this.isSignedIn = false;
    this.currentUser = null;
    this.signInProvider = null;

    this.firebaseRecaptchaVarifier = null;
  }

  /**
   * initialize Firebase
   * @param {Object} config
   */
  initFirebase(config) {
    firebase.initializeApp(config);
    firebase.analytics(); // ついでに analytics
    this.isInitialized = true;
    console.debug('firebase isinitialized');
  }

  /**
   * 認証開始
   * @param {string} bntElementId
   * @param {string} phoneNumber
   *
   */
  signInWithPhoneNumber(phoneNumber) {
    if (!this.firebaseRecaptchaVarifier) {
      throw new Error(AUTH_ERROR_CODES.RECAPTCHA_NOT_PREPARED);
    }

    return firebase
      .auth()
      .signInWithPhoneNumber(phoneNumber, this.firebaseRecaptchaVarifier)
      .then(confirmationResult => {
        return confirmationResult;
      })
      .catch((error) => {
        throw error;
      });
  }

  /**
   * Create user with email, password
   * @param {string} email
   * @param {string} password
   */
  createUserWithEmailAndPassword(email, password) {
    return firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(userCredential => userCredential)
      .catch(error => {
        throw error;
      });
  }

  /**
   * Login user with email, password
   * @param {string} email
   * @param {string} password
   */
  signInWithEmailAndPassword(email, password) {
    return firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(userCredential => userCredential)
      .catch(error => {
        throw error;
      });
  }

  sendEmailVerification() {
    firebase.auth().currentUser.sendEmailVerification();
  }

  /**
   * Authenticate current user by password
   * @param {string} password
   */
  reauthenticateWithPassword(password) {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('User is not logged in');
    }
    const credential = firebase
      .auth
      .EmailAuthProvider
      .credential(currentUser.email, password);

    return firebase
      .auth()
      .currentUser
      .reauthenticateWithCredential(credential);
  }

  /**
   * Update new password
   * @param {string} password
   */
  updatePassword(password) {
    const currentUser = this.currentUser;
    if (!currentUser) {
      throw new Error('User is not logged in');
    }

    return firebase
      .auth()
      .currentUser
      .updatePassword(password);
  }

  /**
   * Update phoneNumber to current user
   * @param {string} phoneNumber
   */
  linkWithPhoneNumber(phoneNumber) {
    if (!this.firebaseRecaptchaVarifier) {
      throw new Error(AUTH_ERROR_CODES.RECAPTCHA_NOT_PREPARED);
    }

    return firebase
      .auth()
      .currentUser
      .linkWithPhoneNumber(phoneNumber, this.firebaseRecaptchaVarifier)
      .then(confirmationResult => confirmationResult)
      .catch(error => {
        throw error;
      });
  }

  /**
   * firebase Id token 取得
   * @return {Promise<string>} token
   */
  setCurrentUser(currentUser) {
    this.currentUser = currentUser;
  }

  /**
   * サインアウト
   */
  signOut() {
    if (!this.currentUser) {
      throw new Error(AUTH_ERROR_CODES.USER_NOT_SIGNED_IN);
    }

    firebase.auth().signOut();
    this.currentUser = null;
    this.signInProvider = null;
    PubSub.clearAllSubscriptions();
    closeCurrentSocket();
  }

  deleteCurrentUser() {
    return firebase
      .auth()
      .currentUser
      .delete()
  }

  /**
   * firebase Id token 取得
   * @return {Promise<string>} token
   */
  getIdToken() {
    if (!this.currentUser) {
      throw new Error(AUTH_ERROR_CODES.USER_NOT_SIGNED_IN);
    }
    return this.currentUser.getIdToken();
  }

  /**
   * サイン in/out ハンドラ
   * @param {Function} onSignIn サインイン時
   * @param {Function} onSignOut サインアウト時
   * @param {Function} onChanged いずれにせよ実行
   */
  addStateChangedHandler(/*onSignIn = () => {}, onSignOut = () => {}, onChanged = () => {}*/) {
    console.error('DEPRECATED');
    //   firebase.auth().onAuthStateChanged((user) => {
    //     // 状態変わったらいずれにせよ実行
    //     onChanged();
    //     if (user) {
    //       this.isSignedIn = true;
    //       this.currentUser = user;
    //       // sign-in
    //       console.debug('AuthBase.addAuthStateChangedHandler() : on sign in');
    //       onSignIn(user);
    //     } else {
    //       this.isSignedIn = false;
    //       this.currentUser = null;
    //       // sing-out
    //       console.debug('AuthBase.addAuthStateChangedHandler() : on sign out');
    //       onSignOut();
    //     }
    //   });
  }

  /*
   * custom hooks
   * Firebase の認証状態HOOK
   */
  useAuthState() {
    const [user, setUser] = useState(null);

    useEffect(() => {
      firebase.auth().onAuthStateChanged((firebaseUser) => {
        if (firebaseUser) {
          this.isSignedIn = true;
          this.currentUser = firebaseUser;
        } else {
          this.isSignedIn = false;
          this.currentUser = null;
        }
        setUser(firebaseUser);
      });
    }, []);
    return user;
  }

  /*
   * GET Auth status
   * Firebase の認証状態
   *
   * statusCode:
   *   -1: default
   *    0: sign-out
   *    1: sign-in
   */
  getUserAuthStatus() {
    const [user, setUser] = useState({ statusCode: -1 });

    useEffect(() => {
      firebase.auth().onAuthStateChanged(async (firebaseUser) => {
        let statusCode = -1;
        if (firebaseUser) {
          const token = await firebaseUser.getIdToken();
          const tokenDecode = jwt_decode(token);
          this.signInProvider = tokenDecode?.firebase?.sign_in_provider;
          this.isSignedIn = true;
          this.currentUser = firebaseUser;
          statusCode = 1;
        } else {
          this.isSignedIn = false;
          this.currentUser = null;
          statusCode = 0;
        }
        setUser({ ...firebaseUser, statusCode });
      });
    }, []);
    return user;
  }
}

/**
 * ============================
 * firebase auth 認証用 reCaptcha 設定コンポーネント
 * ============================
 * @return {React.Component}
 */

const RecaptchaContainer = (props) => {
  const recaptchaRef = useRef(null);

  useEffect(() => {
    // console.debug('RecaptchaContainer did mount ', recaptchaRef.current.innerHTML);
    if (!props.authService.firebaseRecaptchaVarifier) {
      // invisible reCAPTCHA
      props.authService.firebaseRecaptchaVarifier = new firebase.auth.RecaptchaVerifier(
        recaptchaRef.current,
        {
          size: 'invisible',
          callback: (response) => {
            // reCAPTCHA solved, allow signInWithPhoneNumber.
            console.debug('reCAPTCHA resolved.', response);
          },
        }
      );
    }

    // cleanup
    return () => {
      if (props.authService.firebaseRecaptchaVarifier) {
        props.authService.firebaseRecaptchaVarifier.clear();
      }
      props.authService.firebaseRecaptchaVarifier = null;
      // console.debug('RecaptchaContainer did unmount ', recaptchaRef.current.innerHTML);
    };
  }, []);
  return (
    <div>
      <span ref={recaptchaRef} />
    </div>
  );
};
RecaptchaContainer.propTypes = {
  authService: PropTypes.instanceOf(AuthBase),
};
export { RecaptchaContainer };
